import { useVerifyResult } from "./useVerifyResult";
import { useFallacy } from "./useFallacy";
import { useExplanation } from "./useExplanation";
import { useInsights, type GroupedExpertInsights } from "./useInsights";
import { useEvidenceMetadata } from "./useEvidenceMetadata";
import type { EvidenceMetadata } from "@shared/types/claims.ts";
import type { ApiError } from "../../lib/errors";
import type {
  ClaimVerdict,
  EvidenceSource,
  ExplanationResult,
  FallacyResult,
  VerifyResponse,
} from "../../types/claims";

function sortEvidence(evidence: EvidenceSource[]): EvidenceSource[] {
  return [...evidence].sort((a, b) => b.cross_design_quality - a.cross_design_quality);
}

interface UseResultResult {
  result: VerifyResponse | null;
  isLoading: boolean;
  apiError: ApiError | null;
  claimId: string | undefined;
  userEventVerificationId: string | undefined;
  sortedEvidence: EvidenceSource[];
  fallacy: FallacyResult | null;
  isFallacyLoading: boolean;
  explanation: ExplanationResult | null;
  isExplanationLoading: boolean;
  groupedInsights: GroupedExpertInsights[];
  isInsightsLoading: boolean;
  evidenceMetadata: Map<string, EvidenceMetadata>;
  /** There is nothing to show for the claim but the way to ask for it to be looked into. */
  hasNoCoverage: boolean;
}

/**
 * Whether a result has nothing to show but the way to ask for the claim to be looked into. A
 * stored INSUFFICIENT with no evidence is a verification from before NO_COVERAGE existed.
 */
export function isWithoutCoverage(verdict: ClaimVerdict, evidenceCount: number): boolean {
  return verdict === "NO_COVERAGE" || (verdict === "INSUFFICIENT" && evidenceCount === 0);
}

export function useResult(): UseResultResult {
  // URL param (:userEventVerificationId) is read and fetched by useVerifyResult.
  const { result, isLoading, apiError, userEventVerificationId } = useVerifyResult();

  // Pipeline ordering: fallacy first, then explanation (server gates on
  // fallacy via 409). Both keyed by user_event_verification_id — the server
  // resolves it to the canonical verification internally.
  // Skip both when there's no evidence to ground a fallacy analysis or to explain: forcing the
  // LLM produces tautologies.
  const evidenceCount = result?.evidence?.length ?? 0;
  const enrichmentEnabled = evidenceCount > 0;
  const hasNoCoverage =
    result !== null && isWithoutCoverage(result.claim_verdict.verdict, evidenceCount);
  const { fallacy, isLoading: isFallacyLoading } = useFallacy(
    userEventVerificationId,
    enrichmentEnabled
  );
  const { explanation, isLoading: isExplanationLoading } = useExplanation(
    userEventVerificationId,
    enrichmentEnabled && fallacy !== null
  );
  // Insights are independent of the explanation pipeline — fetched as soon as
  // the verification id is known.
  const { groupedInsights, isLoading: isInsightsLoading } = useInsights(userEventVerificationId);

  const evidenceMetadata = useEvidenceMetadata(userEventVerificationId);

  const sortedEvidence = result ? sortEvidence(result.evidence ?? []) : [];

  return {
    result,
    isLoading,
    apiError,
    claimId: result?.claim_id,
    userEventVerificationId,
    sortedEvidence,
    fallacy,
    isFallacyLoading,
    explanation,
    isExplanationLoading,
    groupedInsights,
    isInsightsLoading,
    evidenceMetadata,
    hasNoCoverage,
  };
}
